import { FC, Fragment, useMemo, useState } from 'react';
import { Alert, Card, Col, Form, Row } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import caresLogoWide from '../../assets/images/cares/logo-wide.png';
import authCover from '../../assets/images/cares/auth-cover.png';
import { getCaresShellBootstrap } from './shell/bootstrap';
import './cares-authentication.scss';

const CaresResetPassword: FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const csrfToken = useMemo(() => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '', []);
  const bootstrap = getCaresShellBootstrap();
  const validationMessages = Object.values(bootstrap.validationErrors ?? {}).flat();
  const emailError = bootstrap.validationErrors?.email?.[0];
  const passwordError = bootstrap.validationErrors?.password?.[0];
  const passwordConfirmationError = bootstrap.validationErrors?.password_confirmation?.[0];
  const emailErrorId = emailError ? 'cares-reset-password-email-error' : undefined;
  const passwordErrorId = passwordError ? 'cares-reset-password-password-error' : undefined;
  const passwordConfirmationErrorId = passwordConfirmationError ? 'cares-reset-password-confirmation-error' : undefined;
  const [email, setEmail] = useState(() => bootstrap.authPage.email ?? '');
  const { token: pathToken = '' } = useParams();
  const token = useMemo(() => {
    if (typeof window === 'undefined') {
      return pathToken;
    }

    return new URLSearchParams(window.location.search).get('token') ?? pathToken;
  }, [pathToken]);

  return (
    <Fragment>
      <Row className="authentication cares-authentication mx-0 bg-white">
        <Col xxl={5} xl={5} lg={12}>
          <Row className="justify-content-center align-items-center h-100">
            <Col xxl={6} xl={9} lg={6} md={6} sm={8} className="col-12">
              <Card className="custom-card shadow-none my-4">
                <Card.Body className="p-4">
                  <div className="mb-3 d-flex justify-content-center">
                    <Link to="/">
                      <img src={caresLogoWide} alt="CARES logo" className="authentication-brand desktop-logo" />
                      <img src={caresLogoWide} alt="CARES logo" className="authentication-brand desktop-dark" />
                    </Link>
                  </div>
                  <p className="h5 mb-2 text-center">Create a new password</p>
                  <p className="mb-4 text-muted op-7 fw-normal text-center">
                    Use the reset link from your email, then choose a new password for your CARES account.
                  </p>

                  {validationMessages.length > 0 ? (
                    <Alert variant="danger" role="alert">
                      {validationMessages.map((message, index) => (
                        <div key={`${message}-${index}`}>{message}</div>
                      ))}
                    </Alert>
                  ) : null}

                  <Form method="post" action="/reset-password">
                    <input type="hidden" name="_token" value={csrfToken} />
                    <input type="hidden" name="token" value={token} />
                    <Row className="gy-3">
                      <Col xl={12}>
                        <Form.Label htmlFor="cares-reset-password-email" className="text-default">
                          Email
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          size="lg"
                          id="cares-reset-password-email"
                          placeholder="you@example.com"
                          autoComplete="email"
                          value={email}
                          onChange={(event) => setEmail(event.target.value)}
                          isInvalid={Boolean(emailError)}
                          aria-describedby={emailErrorId}
                          required
                          autoFocus
                        />
                        {emailError ? <Form.Control.Feedback id={emailErrorId} type="invalid" className="d-block">{emailError}</Form.Control.Feedback> : null}
                      </Col>
                      <Col xl={12}>
                        <Form.Label htmlFor="cares-reset-password-password" className="text-default">
                          New Password
                        </Form.Label>
                        <div className="position-relative authentication-input">
                          <Form.Control
                            type={showPassword ? 'text' : 'password'}
                            name="password"
                            size="lg"
                            id="cares-reset-password-password"
                            placeholder="new password"
                            autoComplete="new-password"
                            isInvalid={Boolean(passwordError)}
                            aria-describedby={passwordErrorId}
                            required
                          />
                          <button
                            type="button"
                            className="show-password-button text-muted bg-transparent border-0"
                            onClick={() => setShowPassword((value) => !value)}
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <i className="ri-eye-line align-middle"></i>
                            ) : (
                              <i className="ri-eye-off-line align-middle"></i>
                            )}
                          </button>
                        </div>
                        {passwordError ? <Form.Control.Feedback id={passwordErrorId} type="invalid" className="d-block">{passwordError}</Form.Control.Feedback> : null}
                      </Col>
                      <Col xl={12}>
                        <Form.Label htmlFor="cares-reset-password-confirmation" className="text-default">
                          Confirm New Password
                        </Form.Label>
                        <div className="position-relative authentication-input">
                          <Form.Control
                            type={showPasswordConfirmation ? 'text' : 'password'}
                            name="password_confirmation"
                            size="lg"
                            id="cares-reset-password-confirmation"
                            placeholder="confirm new password"
                            autoComplete="new-password"
                            isInvalid={Boolean(passwordConfirmationError)}
                            aria-describedby={passwordConfirmationErrorId}
                            required
                          />
                          <button
                            type="button"
                            className="show-password-button text-muted bg-transparent border-0"
                            onClick={() => setShowPasswordConfirmation((value) => !value)}
                            aria-label={showPasswordConfirmation ? 'Hide password confirmation' : 'Show password confirmation'}
                          >
                            {showPasswordConfirmation ? (
                              <i className="ri-eye-line align-middle"></i>
                            ) : (
                              <i className="ri-eye-off-line align-middle"></i>
                            )}
                          </button>
                        </div>
                        {passwordConfirmationError ? <Form.Control.Feedback id={passwordConfirmationErrorId} type="invalid" className="d-block">{passwordConfirmationError}</Form.Control.Feedback> : null}
                      </Col>
                    </Row>

                    <div className="d-grid mt-4">
                      <button type="submit" className="btn btn-lg btn-primary">
                        Reset Password
                      </button>
                    </div>
                  </Form>

                  <div className="text-center">
                    <p className="text-muted mt-3 mb-0">
                      Need a fresh link? <a href="/forgot-password" className="text-primary">Request another reset email</a>
                    </p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
        <Col xxl={7} xl={7} lg={7} className="d-xl-block d-none px-0">
          <div className="authentication-cover bg-primary">
            <div>
              <div className="authentication-cover-image">
                <img src={authCover} alt="CARES clinical scheduling workspace" />
              </div>
              <div className="text-center mb-4">
                <h1 className="text-fixed-white">Reset Your Password</h1>
                <p className="text-fixed-white op-8 mb-0">
                  Keep this page open while CARES securely validates your reset link and updates your password.
                </p>
              </div>
              <div className="btn-list text-center">
                <a href="/login" className="btn btn-icon authentication-cover-icon btn-wave" aria-label="Go to CARES sign in">
                  <i className="ri-login-box-line fw-bold"></i>
                </a>
                <Link to="/" className="btn btn-icon authentication-cover-icon btn-wave" aria-label="Go to CARES home">
                  <i className="ri-home-4-line fw-bold"></i>
                </Link>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Fragment>
  );
};

export default CaresResetPassword;
